/* eslint-disable @atlaskit/design-system/no-unsafe-style-overrides */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { cssMap, css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl-next';

import Button, { IconButton } from '@atlaskit/button/new';
import { jsx, css } from '@atlaskit/css';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Box } from '@atlaskit/primitives/compiled';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { token } from '@atlaskit/tokens';

import messages from './messages';

type Props = { autoFocus?: boolean; onCancel: () => void; shape?: 'circle' | 'default' };

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const escTextStylesUnbounded = cssUnbounded({
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	padding: '3px',
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '5px',
});

// no tokens for 3px exist, so using raw values
const escTextStyles = css({
	borderRadius: token('radius.small'),
	color: token('color.text.subtle'),
	backgroundColor: token('color.background.neutral'),
	fontWeight: token('font.weight.regular'),
});

const inlineStyles = cssMap({
	container: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		button: { border: `${token('border.width')} solid ${token('color.border')}` },
	},
	escapeLabel: {
		paddingLeft: token('space.050'),
		paddingRight: token('space.050'),
		paddingTop: token('space.025'),
		paddingBottom: token('space.025'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		color: token('color.text.subtlest'),
		borderRadius: token('radius.small'),
		font: token('font.body.small'),
	},
	codeMd: {
		backgroundColor: token('color.background.neutral.hovered'),
		borderRadius: token('radius.small'),
		height: '20px',
		width: '27px',
		textAlign: 'center',
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
		display: 'inline-block',
		alignContent: 'center',
	},
});

export const CancelEscButton = ({ onCancel, autoFocus, shape = 'default' }: Props) => {
	const { formatMessage } = useIntl();
	return fg('aifc_create_enabled') ? (
		<Inline space="space.100" alignBlock="center" xcss={inlineStyles.container}>
			<Box
				as="span"
				xcss={
					fg('platform_editor_ai_aifc_patch_beta') ? inlineStyles.escapeLabel : inlineStyles.codeMd
				}
				key={'escape-command-text'}
			>
				{formatMessage(messages.labelEsc)}
			</Box>
			<IconButton
				label={
					fg('platform_editor_ai_aifc_patch_beta')
						? formatMessage(messages.labelCancel)
						: 'video stop overlay icon'
				}
				icon={() => (
					<VideoStopOverlayIcon
						label={fg('platform_editor_ai_aifc_patch_beta') ? '' : 'video-stop-overlay-icon'}
						color={token('color.icon.danger')}
					/>
				)}
				shape={shape}
				onClick={onCancel}
				autoFocus={autoFocus}
				appearance="subtle"
			/>
		</Inline>
	) : (
		<Button autoFocus={autoFocus} testId="cancel-esc-button" type="button" onClick={onCancel}>
			{formatMessage(messages.labelCancel)}
			{/* ignore use-primitives-text below as it affects focus and all styles inside and outside the button */}
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text -- Ignored via go/DSP-18766 */}
			<span css={[escTextStyles, escTextStylesUnbounded]}>{formatMessage(messages.labelEsc)}</span>
		</Button>
	);
};
